import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { BaseEntityResource } from '../../core/abstractions/base-entity.resource';
import { ApiResponseModel } from '../../models/core/api-response.model';
import { AuthResponseModel } from '../../models/core/auth-response.model';
import { PostValueResponseModel } from '../../models/core/post-value-response.model';
import { ResourceConfig } from '../../models/core/resource.config';
import { GridsSettingsModel } from '../../models/grids-settings.model';
import { LayoutSettingsModel } from '../../models/layout-settings.model';
import { OptionalType } from '../../models/types/optional.type';
import { UserModel } from '../../models/user.model';
import { UserSessionService } from '../user-session.service';
import { RolesResource } from './roles.resource';

@Injectable({
    providedIn: 'root'
})
export class UsersResource extends BaseEntityResource<UserModel> {
    private readonly rolesResource = inject(RolesResource);
    private readonly userSessionService = inject(UserSessionService);

    constructor() {
        super(new ResourceConfig({ baseController: 'users' }));
    }

    /**
     * Updates the user's language.
     */
    updateLanguage(langCode: string): Observable<ApiResponseModel<boolean>> {
        return this._requestService.makePost<ApiResponseModel<boolean>>(this.getUrl('current/language'), langCode);
    }

    /**
     * Updates the current user's password.
     */
    updatePassword(password: string): Observable<ApiResponseModel<boolean>> {
        return this._requestService.makePost<ApiResponseModel<boolean>>(this.getUrl('current/update-password'), password);
    }

    /**
     * Saves user's grids settings.
     */
    saveGridsSettings(data: GridsSettingsModel): Observable<ApiResponseModel<boolean>> {
        return this._requestService.makePost<ApiResponseModel<boolean>>(this.getUrl('current/settings/grids'), data);
    }

    /**
     * Saves user's layout settings.
     */
    saveLayoutSettings(data: LayoutSettingsModel): Observable<ApiResponseModel<boolean>> {
        return this._requestService.makePost<ApiResponseModel<boolean>>(this.getUrl('current/settings/layout'), data);
    }

    /**
     * Retrieves user's grids settings.
     */
    getGridSettings(): Observable<OptionalType<GridsSettingsModel>> {
        return this._requestService
            .makeGet<ApiResponseModel<string>>(this.getUrl('current/settings/grids'))
            .pipe(map(r => (r.value ? (JSON.parse(r.value) as GridsSettingsModel) : undefined)));
    }

    /**
     * Retrieves user's layout settings with fallback to the default layout settings saved for the user's role.
     */
    getLayoutSettings(): Observable<LayoutSettingsModel> {
        // For netvote userrole always default layout for role
        if (this.userSessionService.hasNetvoteRole) {
            return this.rolesResource.getLayoutSettings(this.userSessionService.roleId);
        }

        return this._requestService.makeGet<ApiResponseModel<string>>(this.getUrl('current/settings/layout')).pipe(
            map(r => (r.value ? (JSON.parse(r.value) as LayoutSettingsModel) : undefined)),
            switchMap(m => (m ? of(m) : this.rolesResource.getLayoutSettings(this.userSessionService.roleId)))
        );
    }

    /**
     * Remove user's layout settings.
     */
    removeLayoutSettings(): Observable<ApiResponseModel<boolean>> {
        return this._requestService.makeDelete<ApiResponseModel<boolean>>(this.getUrl('current/settings/layout'), undefined);
    }

    /**
     * Retrieves the current user's information.
     */
    getUserInfo(): Observable<ApiResponseModel<AuthResponseModel>> {
        return this._requestService.makeGet<ApiResponseModel<AuthResponseModel>>(this.getUrl('current/info'));
    }

    /**
     * Accepts the current shareholder's terms of service
     */
    acceptTos(): Observable<ApiResponseModel<PostValueResponseModel<Date>>> {
        return this._requestService.makePost<ApiResponseModel<PostValueResponseModel<Date>>>(this.getUrl('current/accept-tos'));
    }
}
